<template>
    <div class="create-order">
        <template v-if="isUploadWorkOrder && !uploadWorkOrder.success">
            <div class="card">
                <div class="card-header py-2">Upload Work Order</div>
                <div class="card-body pt-3">
                    <div class="row g-3">
                        <div class="col-md-12">
                            <label for="description" class="form-label">Select Work Order File</label>
                            <ImageUploader
                                ref="uploadWordOrder"
                                v-model="uploadWorkOrder.file"
                                :multiple="false"
                                @filesChanged="handleWorkOrderFileChange"
                            />

                            <!-- Display processing note -->
                            <div v-if="uploadWorkOrder.processing" class="alert alert-info d-flex align-items-center" role="alert">
                                <div class="spinner-border spinner-border-sm me-2" role="status">
                                    <span class="visually-hidden">Loading...</span>
                                </div>
                                Analyzing your work order... This will just take a moment.
                            </div>

                            <!-- Display error message -->
                            <div v-if="uploadWorkOrder.error" class="alert alert-danger" role="alert">
                                {{ uploadWorkOrder.error }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template v-else>
            <StepBar :steps="['Customer', 'Services', 'Attachments', 'Summary']" />
            <Form @submit="createOrder">
                <div class="row">
                    <div class="col-md-8" ref="orderDetailsContainer">
                        <div data-section="0">
                            <div class="card">
                                <div class="card-header d-flex justify-content-between align-items-center py-2">
                                    <div>General Information</div>
                                    <div class="d-flex gap-2">
                                        <!-- Desktop button -->
                                        <div class="d-none d-lg-flex gap-2">
                                            <button type="button"
                                                class="btn btn-sm btn-theme"
                                                data-bs-toggle="modal"
                                                data-bs-target="#createAddressModal"
                                                :disabled="!(order.user_id && String(order.user_id).trim())"
                                                :class="{'disabled cursor-not-allowed': !(order.user_id && String(order.user_id).trim())}"
                                            >Add New Address</button>
                                            <a @click="$goToPage('/admin/users/create?type=business')" class="btn btn-sm btn-theme">Create Business</a>
                                            <a @click="createCutomerOpen" class="btn btn-sm btn-theme">Create Customer</a>
                                        </div>
                                        <!-- Responsive options -->
                                        <div class="d-none d-sm-flex d-lg-none">
                                            <ul class="nav my-md-0 hm-fs-10">
                                                <li class="dropdown">
                                                    <a href="#" class="nav-link text-theme p-2" data-bs-toggle="dropdown">
                                                    <EllipsisVertical class="me-1" />
                                                    </a>
                                                    <ul class="dropdown-menu dropdown-menu-end dropdown-menu-arrow p-0">
                                                        <li>
                                                            <a @click="createCutomerOpen" class="dropdown-item cursor-pointer hm-fs-11">
                                                                <span class="text-theme me-1">
                                                                    <UserPlus :size="16" :stroke-width="2" class="me-1" />
                                                                </span>
                                                                <span class="text-theme">Create Customer</span>
                                                            </a>
                                                        </li>
                                                        <li>
                                                            <a @click="$goToPage('/admin/users/create?type=business')" class="dropdown-item cursor-pointer hm-fs-11">
                                                                <span class="text-theme me-1">
                                                                    <Building2 :size="16" :stroke-width="2" class="me-1"/>
                                                                </span>
                                                                <span class="text-theme">Create Business</span>
                                                            </a>
                                                        </li>
                                                        <li>
                                                            <a
                                                                class="dropdown-item cursor-pointer hm-fs-11"
                                                                data-bs-toggle="modal"
                                                                data-bs-target="#createAddressModal"
                                                                :class="{ 'disabled cursor-not-allowed': !(order.user_id && String(order.user_id).trim()) }"
                                                            >
                                                                <span class="text-theme me-1">
                                                                    <MapPinPlus :size="16" :stroke-width="2" class="me-1" />
                                                                </span>
                                                                <span class="text-theme">Create Address</span>
                                                            </a>
                                                        </li>
                                                    </ul>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                <div class="card-body">
                                    <div class="row g-3">
                                        <div class="col-md-6">
                                            <label for="user" class="form-label">Select Customer <span class="text-danger">*</span></label>
                                            <VueSelect
                                                name="user_id"
                                                v-model="order.user_id"
                                                :class="{ 'is-invalid': !isValidated && order.user_id==='' }"
                                                :items="userOptions"
                                                :placeholder="'Select Customer'"
                                                :searchable="true"
                                                @selectOption="getContactPersons($event)"
                                                :key="'user_id'"
                                            ></VueSelect>
                                            <div class="is-invalid invalid-feedback">This field is required.</div>
                                        </div>
                                        <template v-if="businessOrIndividual === 'business'">
                                            <div class="col-md-6">
                                                <label for="contact_person" class="form-label">Business Contact Person</label>
                                                <VueSelect
                                                    v-model="order.business_contact_person"
                                                    :items="contactPersonOptions"
                                                    :placeholder="'Select Contact Person'"
                                                    :searchable="true"
                                                    :key="'business_contact_person'"
                                                ></VueSelect>
                                            </div>
                                            <div class="col-md-6">
                                                <label for="contact_person" class="form-label">Billing Address</label>
                                                <VueSelect
                                                    v-model="order.business_billing_address"
                                                    :items="[{id: order.business_billing_address, name: order.business_billing_address}]"
                                                    :placeholder="'Select Billing Address'"
                                                    :searchable="false"
                                                    :disabled="true"
                                                ></VueSelect>
                                                <div class="is-invalid invalid-feedback">This field is required.</div>
                                            </div>
                                        </template>

                                        <div class="col-md-6">
                                            <label for="address" class="form-label">Select Address <span class="text-danger">*</span></label>
                                            <!-- <Select2
                                                v-model="order.address_id"
                                                :options="addressOptions"
                                                :settings="{ placeholder: 'Select Address', selectionCssClass: 'create-order-address-dropdown'}"
                                                @select="getBusinessAddressContactPerson($event)"
                                                :class="{ 'is-invalid': !isValidated && order.address_id==='' }"
                                            /> -->
                                            <VueSelect
                                                v-model="order.address_id"
                                                :items="addressOptions"
                                                :placeholder="'Select Address'"
                                                :searchable="true"
                                                :class="{ 'is-invalid': !isValidated && order.address_id==='' }"
                                                @selectOption="getBusinessAddressContactPerson($event)"
                                            ></VueSelect>
                                            <div class="is-invalid invalid-feedback">This field is required.</div>
                                        </div>
                                        <!-- <div class="col-md-2 align-bottom pt-4">
                                            <button type="button"
                                                class="btn btn-theme rounded-1 mt-1"
                                                data-bs-toggle="modal"
                                                data-bs-target="#createAddressModal"
                                                :disabled="!(order.user_id && String(order.user_id).trim())"
                                                :class="{'disabled cursor-not-allowed': !(order.user_id && String(order.user_id).trim())}"
                                            >Add New Address</button>
                                        </div> -->

                                        <div v-if="businessOrIndividual === 'business'" class="col-md-6">
                                            <label for="address" class="form-label">Select Contact Person</label>
                                            <!-- <Select2
                                                v-model="order.address_contact_person"
                                                :options="businessAddressContactPersonOptions"
                                                :settings="{ placeholder: 'Select Contact Person'}"
                                                :class="{ 'is-invalid': !isValidated && order.address_contact_person==='' }"
                                            /> -->
                                            <VueSelect
                                                v-model="order.address_contact_person"
                                                :items="businessAddressContactPersonOptions"
                                                :placeholder="'Select Contact Person'"
                                                :searchable="true"
                                                :class="{ 'is-invalid': !isValidated && order.address_contact_person==='' }"
                                            ></VueSelect>
                                            <!-- <div class="is-invalid invalid-feedback">This field is required.</div> -->
                                        </div>
                                    </div>
                                    <div class="row mt-1 g-3">
                                        <div class="col-md-3">
                                            <label for="order_date" class="form-label">Allocation Date <span class="text-danger">*</span></label>
                                            <BookingDatePicker
                                                v-model="order.booking_date"
                                                :format="'dd-MM-yyyy'"
                                                :time-picker="false"
                                                :auto-apply="true"
                                            ></BookingDatePicker>
                                        </div>
                                        <div class="col-md-3">
                                            <label for="order_time" class="form-label">Order Time</label>
                                            <!-- <VueDatePicker
                                                v-model="order.booking_time"
                                                time-picker
                                                :is-24="false"
                                                :clearable="false"
                                                :minutes-grid-increment="10"
                                                :minutes-increment="15"
                                            >
                                                <template #input-icon>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-clock-11 hm-time-icons"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 9.5 8"/></svg>
                                                </template>
                                            </VueDatePicker> -->
                                            <TimePicker v-model="order.booking_time" placeholder="Select Time" />
                                        </div>
                                        <div class="col-md-6">
                                            <label class="form-label">Task Assign To</label>
                                            <!-- <SelectHandyman
                                                v-model="order.handyman_ids"
                                                :order-date="$moment(order.booking_date).utc().format('YYYY-MM-DD')"
                                                :order-time="$moment(order.booking_time).format('HH:mm:ss')"
                                            ></SelectHandyman> -->
                                            <VueSelect
                                                v-model="order.handyman_ids"
                                                :items="handymanList"
                                                :multiple="true"
                                                :placeholder="'Select Handyman'"
                                            ></VueSelect>
                                        </div>
                                        <div class="col-md-3">
                                            <label for="order_priority" class="form-label">Order Priority</label>
                                            <!-- <Select2
                                                v-model="order.order_priority"
                                                :options="[{'id': 1, 'text': 'Emergency'}, {'id': 2, 'text': 'High'}, {'id': 3, 'text': 'Medium'}, {'id': 4, 'text': 'Low'}]"
                                                :settings="{ placeholder: 'Select Contact Person'}"
                                                :class="{ 'is-invalid': !isValidated && order.order_priority==='' }"
                                            /> -->
                                            <VueSelect
                                                v-model="order.order_priority"
                                                :class="{ 'is-invalid': !isValidated && order.order_priority==='' }"
                                                :items="[{'id': 1, 'name': 'Emergency'}, {'id': 2, 'name': 'High'}, {'id': 3, 'name': 'Medium'}, {'id': 4, 'name': 'Low'}]"
                                                :placeholder="'Select Priority'"
                                                :searchable="false"
                                            ></VueSelect>
                                        </div>
                                        <div class="col-md-3">
                                            <label class="form-label">Order Date</label>
                                            <BookingDatePicker
                                                :format="'dd-MM-yyyy'"
                                                :time-picker="false"
                                                :disabled="true"
                                            ></BookingDatePicker>
                                        </div>
                                        <!-- <div class="col-md-4">
                                            <label for="order_type" class="form-label">Order Type</label>
                                            <VueSelect
                                                v-model="order.order_type"
                                                :items="[{id: 'regular', name: 'Regular'}, {id: 'temporary', name: 'Temporary'}]"
                                                :placeholder="'Select Order Type'"
                                                :searchable="false"
                                            ></VueSelect>
                                        </div> -->
                                        <div class="col-md-6">
                                            <label for="access_arrangement" class="form-label">Access Arrangement</label>
                                            <textarea class="form-control" id="access_arrangement" v-model="order.access_arrangement" rows="2" placeholder="Enter Access Arrangement"></textarea>
                                        </div>
                                        <div class="col-md-12">
                                            <label for="special_note" class="form-label">Special Note (For Handyman)</label>
                                            <textarea class="form-control" id="special_note" v-model="order.special_note" rows="2" placeholder="Enter Special Note"></textarea>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Service & Pricing -->
                        <div data-section="1">
                            <div class="card">
                                <div class="card-header d-flex justify-content-between align-items-center py-2">
                                    Service & Pricing
                                    <a
                                        href="javascript:void(0)"
                                        class="cursor-pointer text-theme"
                                        data-bs-toggle="tooltip"
                                        data-bs-placement="top"
                                        data-bs-title="Add More Service"
                                        @click="addMoreServiceItem"
                                    >
                                        <i class="bi bi-plus-circle"></i> Add More
                                    </a>
                                </div>
                                <div class="card-body">
                                    <div class="row">
                                        <div class="col-md-5">
                                            <label class="form-label d-none d-md-block d-lg-block d-xl-block">Service Name</label>
                                        </div>
                                        <div class="col-md-2">
                                            <label class="form-label d-none d-md-block d-lg-block d-xl-block">Item Cost</label>
                                        </div>
                                        <div class="col-md-2">
                                            <label class="form-label d-none d-md-block d-lg-block d-xl-block">Labour Cost</label>
                                        </div>
                                        <!--  <div class="col-md-2">
                                            <label class="form-label d-none d-md-block d-lg-block d-xl-block">Discount(%)</label>
                                        </div> -->
                                        <div class="col-md-3">
                                            <label class="form-label d-none d-md-block d-lg-block d-xl-block">Total</label>
                                        </div>
                                    </div>
                                    <div
                                        class="row mt-1 g-3 co-service-row"
                                        v-for="(item, index) in totalServiceItems" :key="item"
                                    >
                                        <div class="col-md-5" :class="{'mt-0': index === 0}">
                                            <label class="form-label d-block d-sm-block d-md-none d-lg-none d-xl-none">Service Name</label>
                                            <vue-multiselect
                                                v-model="order.services[item - 1].select_service"
                                                :class="{ 'is-invalid': !isValidated && order.services[item - 1].select_service==null }"
                                                :options="serviceOptionList(index)"
                                                placeholder="Search or add new service"
                                                tag-placeholder="Add this as new service"
                                                :taggable="true"
                                                :show-labels="true"
                                                track-by="name"
                                                label="name"
                                                @tag="addNewService($event,index)"
                                                @select="selectService($event,index)"
                                            ></vue-multiselect>
                                            <div class="is-invalid invalid-feedback">This field is required.</div>
                                        </div>
                                        <div class="col-md-2" :class="{'mt-0': index === 0}">
                                            <label class="form-label d-block d-sm-block d-md-none d-lg-none d-xl-none">Item Cost</label>
                                            <input
                                                type="text"
                                                name="amount"
                                                class="form-control"
                                                placeholder="Item Cost"
                                                v-model="order.services[item - 1].amount"
                                                @keypress="allowNumbersOnly"
                                                @keydown="handleSpecialKeys"
                                                :class="{ 'is-invalid': !isValidated && order.services[item - 1].amount==='' }"
                                            >
                                            <div class="is-invalid invalid-feedback">This field is required.</div>
                                        </div>
                                        <div class="col-md-2" :class="{'mt-0': index === 0}">
                                            <label class="form-label d-block d-sm-block d-md-none d-lg-none d-xl-none">Labour Cost</label>
                                            <input
                                                type="text"
                                                name="labour_cost"
                                                class="form-control"
                                                placeholder="Labour Cost"
                                                v-model="order.services[item - 1].labour_cost"
                                                @keypress="allowNumbersOnly"
                                                @keydown="handleSpecialKeys"
                                                :class="{ 'is-invalid': !isValidated && order.services[item - 1].labour_cost==='' }"
                                            >
                                            <div class="is-invalid invalid-feedback">This field is required.</div>
                                        </div>
                                        <div :class="[Object.keys(order.services).length > 1 ? 'col-md-2' : 'col-md-3', {'mt-0': index === 0}]">
                                            <label class="form-label d-block d-sm-block d-md-none d-lg-none d-xl-none">Total</label>
                                            <div>£{{ order.services[item - 1].total_amount }}</div>
                                        </div>
                                        <div
                                            v-if="Object.keys(order.services).length > 1"
                                            class="col-md-1"
                                            :class="{'mt-0': index === 0}"
                                        >
                                            <label class="form-label d-block d-sm-block d-md-none d-lg-none d-xl-none">Action</label>
                                            <button type="button" class="btn btn-danger btn-sm" @click="removeServiceItem(item)"><i class="bi bi-trash"></i></button>
                                        </div>
                                    </div>
                                    <table class="table in-table align-middle d-none" id="mannualProduct">
                                        <thead>
                                            <tr>
                                                <th scope="col" width="5%">S.No</th>
                                                <!-- <th scope="col" width="15%">Service Type</th> -->
                                                <th scope="col" width="40%">Service Name</th>
                                                <th scope="col" width="15%" class="text-center">Item Cost</th>
                                                <th scope="col" width="15%" class="text-center">Labour Cost</th>
                                                <!-- <th scope="col">Quantity</th> -->
                                                <th scope="col" width="15%" class="text-center">Discount(%)</th>
                                                <!-- <th scope="col">VAT (in %)</th> -->
                                                <th scope="col" width="10%" class="text-center">Net Amount</th>
                                                <th>&nbsp;</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(item, index) in totalServiceItems" :key="item">
                                                <td>{{ item }}</td>
                                                <td>
                                                    <!-- If selected service_type is service then show service list -->
                                                    <template v-if="order.services[item - 1].service_type === 'service'">
                                                        <vue-multiselect
                                                            v-model="order.services[item - 1].select_service"
                                                            :class="{ 'is-invalid': !isValidated && order.services[item - 1].select_service==null }"
                                                            :options="serviceOptionList(index)"
                                                            placeholder="Search or add new service"
                                                            tag-placeholder="Add this as new service"
                                                            :taggable="true"
                                                            :show-labels="true"
                                                            track-by="name"
                                                            label="name"
                                                            @tag="addNewService($event,index)"
                                                            @select="selectService($event,index)"
                                                        ></vue-multiselect>
                                                        <div class="is-invalid invalid-feedback">This field is required.</div>
                                                    </template>
                                                    <template v-else-if="order.services[item - 1].service_type === 'package'">
                                                        <!-- If selected service_type is package then show package list -->
                                                        <Select2
                                                            v-model="order.services[item - 1].package_id"
                                                            :options="packageOptions"
                                                            :settings="{ placeholder: 'Select Package'}"
                                                            @select="calculateServiceItemAmount('package', item - 1)"
                                                            :class="{ 'is-invalid': !isValidated && order.services[item - 1].package_id==='' }"
                                                        />
                                                        <div class="is-invalid invalid-feedback">This field is required.</div>
                                                    </template>
                                                    <template v-else>
                                                        <Select2
                                                            :options="[{id: ' ', text: 'Select Service'}]"
                                                            :settings="{ placeholder: 'Select Service'}"
                                                            :class="{ 'is-invalid': !isValidated && order.services[item - 1].service_id==='' }"
                                                        />
                                                        <div class="is-invalid invalid-feedback">This field is required.</div>
                                                    </template>
                                                </td>
                                                <td class="text-center">
                                                    <input
                                                        type="text"
                                                        name="amount"
                                                        class="form-control"
                                                        placeholder="Item Cost"
                                                        v-model="order.services[item - 1].amount"
                                                        @keypress="allowNumbersOnly"
                                                        @keydown="handleSpecialKeys"
                                                        :class="{ 'is-invalid': !isValidated && order.services[item - 1].amount==='' }"
                                                    >
                                                    <div class="is-invalid invalid-feedback">This field is required.</div>
                                                </td>
                                                <td class="text-center">
                                                    <input
                                                        type="text"
                                                        name="labour_cost"
                                                        class="form-control"
                                                        placeholder="Labour Cost"
                                                        v-model="order.services[item - 1].labour_cost"
                                                        @keypress="allowNumbersOnly"
                                                        @keydown="handleSpecialKeys"
                                                        :class="{ 'is-invalid': !isValidated && order.services[item - 1].labour_cost==='' }"
                                                    >
                                                    <div class="is-invalid invalid-feedback">This field is required.</div>
                                                </td>
                                                <td class="text-center">
                                                    <input
                                                        type="text"
                                                        name="discount"
                                                        class="form-control"
                                                        placeholder="Enter discount percentage"
                                                        v-model="order.services[item - 1].discount"
                                                        @input="validateInput('service', (item - 1))"
                                                        @keypress="allowNumbersOnly"
                                                        @keydown="handleSpecialKeys"
                                                    >
                                                </td>
                                                <td class="text-center">
                                                £{{ order.services[item - 1].total_amount }}
                                                </td>
                                                <td class="text-center">
                                                <button
                                                    v-if="
                                                        Object.keys(
                                                            order.services
                                                        ).length > 1
                                                    "
                                                    type="button"
                                                    class="btn btn-sm btn-danger"
                                                    @click="removeOrderItem(item - 1)"
                                                    style="left: 30px"
                                                >
                                                    <i class="bi bi-trash"></i>
                                                </button>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <!-- Comment & Attachment -->
                        <div data-section="2">
                            <div class="card">
                                <div class="card-header d-flex justify-content-between align-items-center py-2">
                                    Comment & Attachment
                                </div>
                                <div class="card-body pt-3">
                                    <div class="row g-3">
                                        <div class="col-md-12">
                                            <label for="comment" class="form-label">Comment</label>
                                            <textarea class="form-control" id="comment" v-model="order.comment" rows="2" placeholder="Enter comment"></textarea>
                                        </div>
                                        <div class="col-md-12">
                                            <label for="description" class="form-label">Attachment</label>
                                            <ImageUploader ref="imageUploader"/>
                                            <!-- <div class="input-group mb-3">
                                                <input type="file" class="form-control" multiple @change="onFileChange">
                                            </div>

                                            <img v-for="previewImage in previewOrderImages"
                                                :src="previewImage"
                                                class="img-thumbnail me-2 mb-2"
                                                style="{ max-height: 150px; max-width: 150px; }"
                                                :key="previewImage"
                                            > -->
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Calculation -->
                        <div data-section="3">
                            <div class="card">
                                <div class="card-header d-flex justify-content-between align-items-center py-2">
                                    Calculation
                                </div>
                                <div class="card-body">
                                    <div class="row g-3">
                                        <div class="col-md-3">
                                            <label for="discount" class="form-label">Total</label>
                                            <input
                                                readonly
                                                type="text"
                                                class="form-control"
                                                v-model="order.amount"
                                            />
                                        </div>
                                        <div class="col-md-3">
                                            <label for="discount" class="form-label">Discount(%)</label>
                                            <input
                                                name="discount"
                                                type="text"
                                                class="form-control"
                                                placeholder="Enter discount"
                                                v-model="order.discount"
                                                @input="validateInput('order', 1)"
                                                @keypress="allowNumbersOnly"
                                                @keydown="handleSpecialKeys"
                                            />
                                        </div>
                                        <div class="col-md-3">
                                            <label for="discount" class="form-label">Discount</label>
                                            <input
                                                type="text"
                                                class="form-control"
                                                readonly
                                                v-model="order.discount_amount"
                                            />
                                        </div>
                                        <div class="col-md-3">
                                            <label for="discount" class="form-label">Amount</label>
                                            <input
                                                readonly
                                                type="text"
                                                class="form-control"
                                                :value="(order.amount - order.discount_amount).toFixed(2)"
                                            />
                                        </div>
                                        <!-- <div
                                            class="d-flex justify-content-between align-items-center"
                                        > -->
                                            <div class="col-md-3">
                                                <label for="discount" class="form-label">VAT(%)</label>
                                                <!-- <input name="discount" type="text" class="form-control" placeholder="Enter discount" readonly v-model="tax"> -->
                                                <!-- <Select2
                                                    v-model="order.tax"
                                                    :options="[
                                                        { id: '0', text: '0' },
                                                        { id: tax, text: tax },
                                                    ]"
                                                    :settings="{
                                                        minimumResultsForSearch:
                                                            'Infinity',
                                                        placeholder: 'Select VAT',
                                                    }"
                                                /> -->
                                                <VueSelect
                                                    v-model="order.tax"
                                                    :items="[{'id': '0', 'name': '0'}, {'id': tax, 'name': tax}]"
                                                    :placeholder="'Select VAT'"
                                                    :searchable="false"
                                                ></VueSelect>
                                            </div>
                                            <div class="col-md-3">
                                                <label for="discount" class="form-label">VAT</label>
                                                <input
                                                    type="text"
                                                    class="form-control"
                                                    readonly
                                                    v-model="order.tax_amount"
                                                />
                                            </div>
                                            <!-- <div class="col-4">
                                                <label
                                                    for="total_amount"
                                                    class="form-label"
                                                    >Round Off
                                                    <span class="text-danger"
                                                        >(-/+)</span
                                                    ></label
                                                >
                                                <input
                                                    name="total_amount"
                                                    type="text"
                                                    class="form-control"
                                                    readonly
                                                    value="0.00"
                                                />
                                            </div> -->
                                        <!-- </div> -->
                                        <div class="col-md-3">
                                            <label for="total_amount" class="form-label">Net Price</label>
                                            <input
                                                name="total_amount"
                                                type="text"
                                                class="form-control"
                                                readonly
                                                :value="'£' + order.total_amount"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Submit button -->
                        <div class="card">
                            <div class="card-body">
                                <button
                                    type="submit"
                                    class="btn btn-theme btn-sm float-end"
                                    v-text="isProcessing ? 'Processing...' : 'Create Order'"
                                    :disabled="isProcessing || !isFormValid"
                                    :class="{'disabled': isProcessing || !isFormValid}"
                                ></button>
                            </div>
                        </div>
                    </div>

                    <!-- Timeline -->
                    <div class="col-md-4 ps-0">
                        <div class="main-card mb-3 card">
                            <div class="card-header d-flex justify-content-between align-items-center py-2">
                                Order Timeline
                            </div>
                            <div class="card-body dashboard">
                                <div class="scroll-area sidebar-timeline" :style="{ height: timelineContainerHeight + 'px' }">
                                    <div class="activity">
                                        <div
                                            v-for="(timeline, index) in histories"
                                            :key="index"
                                            class="activity-item d-flex"
                                        >
                                            <div class="activite-label"></div>
                                            <i class="bi bi-circle-fill activity-badge text-theme align-self-start"></i>
                                            <div class="activity-content">
                                                <h6 class="timeline-title text-capitalize">{{ (timeline.title).replace(/_/g, ' ') }}</h6>
                                                <p class="hm-fs-10 mb-1">{{ dataTimeFormat(timeline.date, 'hh:mm A') }} {{dataTimeFormat(timeline.date, 'DD/MM/YYYY')}} By {{user?.first_name}}</p>
                                                <p
                                                    v-for="(service, idx) in timeline.services"
                                                    :key="idx"
                                                    class="mb-1"
                                                >
                                                    Service <strong>{{ service.name }}</strong> added to the order.
                                                </p>
                                            </div>
                                        </div>
                                        <!-- End activity item-->
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Form>
        </template>
    </div>
    <!-- Create customer modal -->
    <div
        class="modal fade"
        id="createCustomer"
        aria-labelledby="createCustomer"
        aria-hidden="true"
    >
        <div class="modal-dialog modal-xl">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">
                        Create Customer
                    </h5>
                    <button
                        type="button"
                        class="btn-close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                    ></button>
                </div>
                <div class="modal-body">
                    <div class="row g-3">
                        <div class="col-md-2 d-none d-lg-block">
                            <label for="salutation" class="form-label">Select Salutation <span class="text-danger">*</span></label>
                            <Select2
                                name="salutation"
                                v-model="customer.salutation"
                                :options="salutationOption"
                                :settings="{ placeholder: 'Select Salutation', }"
                                :class="{ 'is-invalid': !isCustomerValidated && customer.salutation==='' }"
                            />
                            <div class="is-invalid invalid-feedback">This field is required.</div>
                        </div>
                        <div class="col-md-5">
                            <label for="first_name" class="form-label">First Name<span class="text-danger">*</span></label>
                            <input
                                name="first_name"
                                type="text"
                                class="form-control form-control-sm"
                                placeholder="Enter First Name"
                                v-model="customer.first_name"
                                :class="{ 'is-invalid': !isCustomerValidated && customer.first_name==='' }"
                            >
                            <div class="is-invalid invalid-feedback">This field is required.</div>
                        </div>
                        <div class="col-md-5">
                            <label for="last_name" class="form-label">Last Name<span class="text-danger">*</span></label>
                            <input
                                name="last_name"
                                type="text"
                                class="form-control form-control-sm"
                                placeholder="Enter Last Name"
                                v-model="customer.last_name"
                                :class="{ 'is-invalid': !isCustomerValidated && customer.last_name==='' }"
                            >
                            <div class="is-invalid invalid-feedback">This field is required.</div>
                        </div>
                        <div class="col-md-6">
                            <label for="email" class="form-label">Email Address</label>
                            <input
                                name="email"
                                type="email"
                                class="form-control form-control-sm"
                                placeholder="Enter Email Address"
                                v-model="customer.email"
                            >
                            <div class="is-invalid invalid-feedback">This field is required.</div>
                        </div>
                        <div class="col-md-6">
                            <label for="phone_number" class="form-label">Phone Number<span class="text-danger">*</span></label>
                            <input
                                name="phone_number"
                                type="number"
                                class="form-control form-control-sm"
                                placeholder="Enter Phone Number"
                                v-model="customer.phone_number"
                                :class="{ 'is-invalid': !isCustomerValidated && customer.phone_number==='' }"
                            >
                            <div class="is-invalid invalid-feedback">This field is required.</div>
                        </div>
                        <!-- Address Fileds -->
                        <div class="col-md-4">
                            <label for="address" class="form-label">Address One<span class="text-danger">*</span></label>
                            <input
                                name="address"
                                type="text"
                                class="form-control form-control-sm"
                                placeholder="Enter Address One"
                                v-model="customer.address.address"
                                :class="{ 'is-invalid': !isCustomerValidated && customer.address.address ==='' }"
                            >
                            <div class="is-invalid invalid-feedback">This field is required.</div>
                        </div>
                        <div class="col-md-4">
                            <label for="address_1" class="form-label">Address Two</label>
                            <input
                                name="address_1"
                                type="text"
                                class="form-control form-control-sm"
                                placeholder="Enter Address Two"
                                v-model="customer.address.address_1"
                            >
                            <!-- <div class="is-invalid invalid-feedback">This field is required.</div> -->
                        </div>
                        <div class="col-md-4">
                            <label for="address_2" class="form-label">Address Three</label>
                            <input
                                name="address_2"
                                type="text"
                                class="form-control form-control-sm"
                                placeholder="Enter Address Three (Optional)"
                                v-model="customer.address.address_2"
                            >
                        </div>
                        <div class="col-md-4">
                            <label for="city" class="form-label">City<span class="text-danger">*</span></label>
                            <input
                                name="city"
                                type="text"
                                class="form-control form-control-sm"
                                placeholder="Enter City"
                                v-model="customer.address.city"
                                :class="{ 'is-invalid': !isCustomerValidated && customer.address.city==='' }"
                            >
                            <div class="is-invalid invalid-feedback">This field is required.</div>
                        </div>
                        <div class="col-md-4">
                            <label for="county" class="form-label">County<span class="text-danger">*</span></label>
                            <input
                                name="county"
                                type="text"
                                class="form-control form-control-sm"
                                placeholder="Enter County"
                                v-model="customer.address.county"
                                :class="{ 'is-invalid': !isCustomerValidated && customer.address.county==='' }"
                            >
                            <div class="is-invalid invalid-feedback">This field is required.</div>
                        </div>
                        <div class="col-md-4">
                            <label for="postal_code" class="form-label">Postcode<span class="text-danger">*</span></label>
                            <input
                                name="postal_code"
                                type="text"
                                class="form-control form-control-sm"
                                placeholder="Enter Postcode"
                                v-model="customer.address.postal_code"
                                :class="{ 'is-invalid': !isCustomerValidated && customer.address.postal_code==='' }"
                            >
                            <div class="is-invalid invalid-feedback">This field is required.</div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button
                        type="button"
                        class="btn btn-danger btn-sm"
                        @click="closeCreateCustomer"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="btn btn-theme btn-sm"
                        v-text="isProcessing ? 'Processing...' : 'Create Customer'"
                        :disabled="isProcessing"
                        :class="{'disabled': isProcessing}"
                        @click="processCreateCustomer"
                    >
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- Create address modal -->
    <div class="modal fade" id="createAddressModal" tabindex="-1" aria-labelledby="createAddressModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-xl">
            <form @submit.prevent="processCreateAddress">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="createAddressModalLabel">Create Address</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <div class="row g-3">
                            <div class="col-md-6">
                                <label for="address" class="form-label">Address One<span class="text-danger">*</span></label>
                                <input
                                    name="address"
                                    type="text"
                                    class="form-control form-control-sm"
                                    placeholder="Enter Address One"
                                    v-model="createCustomerAddress.address"
                                    :class="{ 'is-invalid': !isAddressFormValid && getErrorMessage('address') !=='' }"
                                >
                                <div class="is-invalid invalid-feedback">This field is required.</div>
                            </div>
                            <div class="col-md-6">
                                <label for="address_1" class="form-label">Address Two (Optional)</label>
                                <input
                                    name="address_1"
                                    type="text"
                                    class="form-control form-control-sm"
                                    placeholder="Enter Address Two"
                                    v-model="createCustomerAddress.address_1"
                                    :class="{ 'is-invalid': !isAddressFormValid && getErrorMessage('address_1') !=='' }"
                                >
                            </div>
                            <div class="col-md-6">
                                <label for="address_2" class="form-label">Address Three (Optional)</label>
                                <input name="address_2" type="text" class="form-control form-control-sm" placeholder="Enter Address Three" v-model="createCustomerAddress.address_2">
                            </div>
                            <div class="col-md-6">
                                <label for="city" class="form-label">City<span class="text-danger">*</span></label>
                                <input
                                    name="city"
                                    type="text"
                                    class="form-control form-control-sm"
                                    placeholder="Enter City"
                                    v-model="createCustomerAddress.city"
                                    :class="{ 'is-invalid': !isAddressFormValid && getErrorMessage('city') !=='' }"
                                >
                                <div class="is-invalid invalid-feedback">This field is required.</div>
                            </div>
                            <div class="col-md-6">
                                <label for="county" class="form-label">County<span class="text-danger">*</span></label>
                                <input
                                    name="county"
                                    type="text"
                                    class="form-control form-control-sm"
                                    placeholder="Enter County"
                                    v-model="createCustomerAddress.county"
                                    :class="{ 'is-invalid': !isAddressFormValid && getErrorMessage('county') !=='' }"
                                >
                                <div class="is-invalid invalid-feedback">This field is required.</div>
                            </div>
                            <div class="col-md-6">
                                <label for="postal_code" class="form-label">Postcode<span class="text-danger">*</span></label>
                                <input
                                    name="postal_code"
                                    type="text"
                                    class="form-control form-control-sm"
                                    placeholder="Enter Postcode"
                                    v-model="createCustomerAddress.postal_code"
                                    :class="{ 'is-invalid': !isAddressFormValid && getErrorMessage('postal_code') !=='' }"
                                >
                                <div class="is-invalid invalid-feedback">This field is required.</div>
                            </div>
                        </div>
                        <template v-if="businessOrIndividual === 'business'">
                            <div class="row g-3 mt-3" v-for="(ucp, index) in createCustomerAddress.contact_person">
                                <hr>
                                <h4 class="text-capitalize fw-500 mt-0 cp-head item-align-center d-flex">
                                    Contact Person
                                     <div class="ms-3 fs-6 mt-1">
                                        <div class="form-check">
                                            <input class="form-check-input" type="checkbox" :id="'same_as_business_'+index" @change="prefillContactPerson($event, index)">
                                            <label class="form-check-label mt-1" :for="'same_as_business_'+index">Same as business</label>
                                        </div>
                                    </div>
                                </h4>
                                <div class="col-md-6">
                                    <label for="salutation" class="form-label">Person Type <span class="text-danger">*</span></label>
                                    <Select2
                                        name="person_type"
                                        v-model="ucp.person_type"
                                        :options="[{id: ' ', text: 'Person Type'}, {id:'tenant', text: 'Tenant'}, {id:'landlord', text: 'Landlord'}, {id: 'property_owner', text: 'Property Owner'}, {id: 'business', text: 'Business'}]"
                                        :settings="{ placeholder: 'Person Type', minimumResultsForSearch: 'Infinity'}"
                                        :class="{ 'is-invalid': !isAddressFormValid && getErrorMessage('person_type_' + index) !=='' }"
                                    />
                                    <div class="is-invalid invalid-feedback">This field is required.</div>
                                </div>

                                <div class="col-md-6">
                                    <label for="salutation" class="form-label">Select Salutation <span class="text-danger">*</span></label>
                                    <Select2
                                        name="salutation"
                                        v-model="ucp.salutation"
                                        :options="[{id: ' ', text: 'Select Salutation'}, {id:'Mr.', text: 'Mr.'}, {id:'Ms.', text: 'Ms.'}, {id:'Mrs.', text: 'Mrs.'}, {id:'Dr.', text: 'Dr.'}, {id:'Rev.', text: 'Rev.'}, {id:'Prof.', text: 'Prof.'}]"
                                        :settings="{ placeholder: 'Select Salutation', minimumResultsForSearch: 'Infinity'}"
                                        :class="{ 'is-invalid': !isAddressFormValid && getErrorMessage('salutation_' + index) !=='' }"
                                    />
                                    <div class="is-invalid invalid-feedback">This field is required.</div>
                                </div>
                                <div class="col-md-6">
                                    <label for="first_name" class="form-label">First Name<span class="text-danger">*</span></label>
                                    <input
                                        name="first_name"
                                        type="text"
                                        class="form-control form-control-sm"
                                        placeholder="Enter First Name"
                                        v-model="ucp.first_name"
                                        :class="{ 'is-invalid': !isAddressFormValid && getErrorMessage('first_name_' + index) !=='' }"
                                    >
                                    <div class="is-invalid invalid-feedback">This field is required.</div>
                                </div>
                                <div class="col-md-6">
                                    <label for="last_name" class="form-label">Last Name</label>
                                    <input name="last_name" type="text" class="form-control" placeholder="Enter Last Name" v-model="ucp.last_name">
                                </div>
                                <div class="col-md-6">
                                    <label for="email" class="form-label">Email<span class="text-danger">*</span></label>
                                    <input
                                        name="email"
                                        type="email"
                                        class="form-control form-control-sm"
                                        placeholder="Enter Email"
                                        v-model="ucp.email"
                                        :class="{ 'is-invalid': !isAddressFormValid && getErrorMessage('email_' + index) !=='' }"
                                    >
                                    <div class="is-invalid invalid-feedback">This field is required.</div>
                                </div>
                                <div class="col-md-6">
                                    <label for="phone_number" class="form-label">Phone Number 1<span class="text-danger">*</span></label>
                                    <input
                                        name="phone_number"
                                        type="number"
                                        class="form-control form-control-sm"
                                        placeholder="Enter Phone Number"
                                        v-model="ucp.phone_number"
                                        :class="{ 'is-invalid': !isAddressFormValid && getErrorMessage('phone_number_' + index) !=='' }"
                                    >
                                    <div class="is-invalid invalid-feedback">This field is required.</div>
                                </div>
                                <div class="col-md-6">
                                    <label for="phone_number" class="form-label">Phone Number 2</label>
                                    <input name="phone_number" type="number" class="form-control form-control-sm" placeholder="Enter Phone Number 2" v-model="ucp.phone_number_1">
                                </div>
                            </div>
                            <div v-if="createCustomerAddress.contact_person.length === 1" class="d-flex pb-20 mb-3 justify-content-md-end justify-content-center">
                                <button type="button" class="btn btn-sm btn-theme add-more-address-contact-person" @click="addMoreAddressContactPerson"><i class="bi bi-plus-circle pe-1"></i> More</button>
                            </div>

                        </template>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-danger btn-sm" data-bs-dismiss="modal">Cancel</button>
                        <button type="submit" class="btn btn-theme btn-sm" :disabled="createAddressProcessing" :class="{ 'disabled cursor-not-allowed': createAddressProcessing }">Create Address</button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>
<script>
//import Select2 from './partials/Select2.vue';
import Select2 from 'vue3-select2-component';
import moment from 'moment';

import { Form, Field, validate, configure } from 'vee-validate';
import * as Yup from 'yup';
import VueTimepicker from 'vue3-timepicker/src/VueTimepicker.vue'
import BookingDatePicker from "./BookingDatePicker";
import SelectHandyman from "./partials/SelectHandyman";
import VueSelect from "./partials/VueSelect";
import { appMixin } from "../mixin/appMixin";
import ImageUploader from './Order/ImageUploader';
import StepBar from './Order/StepBar.vue';
import TimePicker from './partials/TimePicker.vue';

import {
    EllipsisVertical,
    MapPinPlus,
    UserPlus,
    Building2
} from 'lucide-vue-next';

export default {
    components: {
        Select2,
        Form,
        Field,
        VueTimepicker,
        BookingDatePicker,
        SelectHandyman,
        VueSelect,
        ImageUploader,
        StepBar,
        EllipsisVertical,
        MapPinPlus,
        UserPlus,
        Building2,
        TimePicker
    },
    mixins: [appMixin],
    emits: ['select'], // Declare custom event here
    props: {
        isUploadWorkOrder: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            tax: 20,
            users: [],
            userContactPersons: [],
            addresses: [],
            businessOrIndividual: '', // business or individual
            userOptions: [{id: ' ', text: 'Select Customer', name: 'Select Customer'}],
            addressOptions: [{id: ' ', text: 'Select Address', name: 'Select Address'}],
            contactPersonOptions: [{id: ' ', text: 'Select Contact Person', name: 'Select Contact Person'}],
            serviceTypes: [
                {id: '', 'Text': 'Select'},
                {id: 'service', 'text': 'Services'},
                {id: 'package', 'text': 'Packages'}
            ],
            serviceType: '', // package or service
            services: [],
            packages: [],
            order: {
                user_id: '',
                business_contact_person: '',
                business_billing_address: '',
                address_id: '',
                address_contact_person: '',
                booking_date: new Date(), // add 1 day to the current date
                booking_time: {
                    hours: 7,
                    minutes: 0
                },
                start_at: '',
                end_at: '',
                order_priority: 4,
                comment: '',
                services: [
                    {
                        service_type: 'service',
                        service_id: '',
                        package_id: '',
                        tax: 20,
                        discount: '', // in percentage
                        amount: '',
                        labour_cost: '',
                        discount_amount: 0.00,
                        tax_amount: 0.00,
                        total_amount: 0.00,
                        total_with_tax: 0.00,
                        select_service: null
                    }
                ],
                tax: 20, // in percentage
                discount: 0,
                amount: 0.00,
                item_price: 0.00,
                labour_cost: 0.00,
                discount_amount: 0,
                tax_amount: 0.00,
                total_amount: 0.00,
                files: [],
                handyman_ids: [],
                access_arrangement: '',
                order_type: 'regular', // regular or temporary
                special_note: '',
            },
            totalServiceItems: 1,
            selectedServices: [],
            isProcessing: false,
            isFormValid: true,
            serviceOptions: [],
            packageOptions: [],
            businessAddressContactPersonOptions: [],
            minDatetime: '',
            minDate: '',
            errors: {
                user_id: '',
                business_contact_person: '',
                business_billing_address: '',
                address_id: '',
                address_contact_person: '',
                booking_date: '',
                booking_time: '',
                services: [
                    {
                        service_type: '',
                        service_id: '',
                        package_id: '',
                        amount: '',
                        discount: '', // in percentage
                        total_amount: 0.00,
                    }
                ],
            },
            customer: {
                salutation:'Mr.',
                first_name:'',
                last_name:'',
                email: '',
                phone_number:'',
                address:{
                    address:'',
                    address_1:'',
                    address_2:'',
                    city:'',
                    county:'England',
                    postal_code:''
                }
            },
            salutationOption:[
                {id: '', text: 'Select Salutation'},
                {id: 'Mr.', text: 'Mr.'},
                {id: 'Ms.', text: 'Ms.'},
                {id: 'Mrs.', text: 'Mrs.'},
                {id: 'Dr.', text: 'Dr.'},
                {id: 'Rev.', text: 'Rev.'},
                {id: 'Prof.', text: 'Prof.'},
            ],
            isValidated: true,
            isCustomerValidated: true,
            previewOrderImages: [],
            createCustomerAddress: {
                user_id: '',
                business_or_individual: '',
                address: '',
                address_1: '',
                address_2: '',
                city: '',
                county: 'England',
                postal_code: '',
                contact_person: [
                    {
                        person_type: '',
                        salutation: '',
                        first_name: '',
                        last_name: '',
                        email: '',
                        phone_number: '',
                        phone_number_1: '',
                    },
                ]
            },
            validationAddressErrors: [],
            isAddressFormValid: true,
            createAddressProcessing: false,
            createAddressError: {
                person_type: null,
                salutation: null,
                first_name: null,
                email: null,
                phone_number: null,
                address: null,
                city: null,
                county: null,
                postal_code: null
            },
            selectUser: false,
            timelineContainerHeight: 300,
            timelines:[
                {
                    id: 1,
                    title: 'Order Created',
                    date: new Date(),
                    time: new Date(),
                    status: 'pending',
                },
            ],
            handymanList: [],
            uploadWorkOrder: {
                file: null,                    // The uploaded file object
                fileName: '',                  // Name of the uploaded file
                fileType: '',                  // MIME type (image/jpeg, application/pdf)
                base64Data: '',                // Base64 encoded file data
                processing: false,             // Overall processing state
                extracting: false,             // AI extraction in progress
                error: null,                   // Error message if any
                success: false,                // Success state
                matchedAddress: null,          // Matched address from database
                matchedService: null,          // Matched service from database
                extractedData: {               // Data extracted from AI
                    vat_no: '',
                    date: '',
                    required_by: '',
                    reference: '',
                    status: '',
                    summary: '',
                    service: '',
                    property: '',
                    contractor_name: '',
                    contractor_email: '',
                    property_manager_name: '',
                    property_manager_email: '',
                    property_manager_phone: '',
                    work_order_details: [],
                    sub_total: '',
                    tax_percentage: '',
                    tax_amount: '',
                    total_amount: ''
                }
            }
        }
    },
    created() {
        this.getUsers();
        this.getServicesForOrder();

        // Set the minimum datetime to the current datetime
        this.setMinDatetime();
        this.setMinDate();

        // Add 1 day to the current date into the booking date
        this.order.booking_date = this.moment();
    },
    computed: {
        orderSchema() {
            return Yup.object().shape({
                user_id: Yup.string().required('User is required'),
                business_contact_person: Yup.string().required('Business Contact Person is required'),
                business_billing_address: Yup.string().required('Business Billing Address is required'),
                address_id: Yup.string().required('Address is required'),
                address_contact_person: Yup.string().required('Address Contact Person is required'),
                booking_date: Yup.string().required('Booking Date is required'),
                booking_time: Yup.string().required('Booking Time is required'),
                services: Yup.array().of(
                    Yup.object().shape({
                        service_type: Yup.string().required('Service Type is required'),
                        service_id: Yup.string().required('Service is required'),
                        package_id: Yup.string().required('Package is required'),
                        amount: Yup.string().required('Amount is required'),
                    })
                ),
            });
        },
        handymanOptions() {
            let schedules = this.$store.state.availableSchedules;

            let self = this;
            let options = [];

            const dateToFind = this.$moment(this.order.booking_date).utc().format('YYYY-MM-DD');
            const timeToFind =  this.$moment(`${dateToFind} ${this.order.booking_time.hours}:${this.order.booking_time.minutes} :00`, 'YYYY-MM-DD HH:mm:ss');

            Object.keys(schedules).forEach((handymanId) => {
                schedules[handymanId].forEach((schedule) => {
                    const startTime = this.$moment(`${schedule.schedule_date} ${schedule.start_time}`, 'YYYY-MM-DD HH:mm:ss');
                    const endTime = this.$moment(`${schedule.schedule_date} ${schedule.end_time}`, 'YYYY-MM-DD HH:mm:ss');
                    if (schedule.schedule_date === dateToFind && timeToFind.isBetween(startTime, endTime,undefined, '[]')) {
                        let exists = options.some(function(option) {
                            return option.id === schedule.handyman_id;
                        });
                        // If it doesn't exist, push the new option
                        if (!exists) {
                            options.push({
                                id: schedule.handyman_id,
                                text: schedule.full_name + ' (' + moment(schedule.start_time, 'HH:mm:ss').format('hh:mm A') + ' - ' + moment(schedule.end_time, 'HH:mm:ss').format('hh:mm A') + ')',
                                name: schedule.full_name + ' (' + moment(schedule.start_time, 'HH:mm:ss').format('hh:mm A') + ' - ' + moment(schedule.end_time, 'HH:mm:ss').format('hh:mm A') + ')',
                            });
                        }
                    }
                });
            });

            return options;
        },
        serviceOptionList() {
            return (currentIndex) => {
                let options = [];

                // Get selected services name
                let selectedServicesName = this.order.services
                    .map((service) => {
                        return service ? service.name : null;
                    })
                    .filter((service, index) => {
                        return currentIndex !== index && service !== null && service !== undefined;
                    });

                this.services.forEach((service) => {
                    options.push({
                        id: service.id,
                        name: service.name,
                        price: service.price,
                        labour_cost: service.labour_cost,
                        discount: service.discount
                    });
                });

                // filter the options based on the selected services name
                options = options.filter((option) => {
                    return !selectedServicesName.includes(option.name);
                });

                return options;
            }
        },
        user() {
            return this.$store.getters.getUser;
        },
        histories() {
            let items = this.timelines;

            // Iterate the order services, if name is not empty then push it to the items
            const serviceNames = this.order.services
                .filter((service) => {
                    return service.name !== '' && service.name !== null && service.name !== undefined;
                })
                .map((service) => {
                    return {
                        id: service.id,
                        name: service.name,
                    };
                });
            if(serviceNames.length > 0) {
                // Before push check if id 2 is already exist or not
                // If exist then just update the services
                const existingIndex = items.findIndex(item => item.id === 2);
                if(existingIndex !== -1) {
                    items[existingIndex].services = serviceNames;
                } else {
                    items.push({
                        id: 2,
                        title: 'Service Added',
                        date: new Date(),
                        time: new Date(),
                        services: serviceNames
                    });
                }
            }
            return items;
        }
    },
    mounted() {
        this.getAllAddresses();

        // Set timeline container height
        setTimeout(() => {
            if(this.$refs?.orderDetailsContainer) {
                this.timelineContainerHeight = this.$refs.orderDetailsContainer.offsetHeight;
            }
        }, 3000);
    },
    methods: {
        removeOrderItem(index) {
            this.totalServiceItems--;
            this.order.services.splice(index, 1);
            this.order.services = this.order.services.filter(function(){return true;});
        },
        closeCreateCustomer(){
            $('#createCustomer').modal('hide')
        },
        createCutomerOpen() {
            $('#createCustomer').modal('show');
        },
        processCreateCustomer(){
            if(!this.validateCustomerForm()) {
                this.isCustomerValidated = false;
                return;
            }
            this.isCustomerValidated = true;

            this.isProcessing = true;

            const config = {
            }

            axios.post('/admin/users', this.customer, config)
                .then(response => {
                    if (response.data.status === 'success') {
                        const user_id = response.data.user_id;
                            axios.post('/admin/users/store-address/' + user_id, this.customer.address, config)
                            .then(response => {
                                this.isProcessing = false;
                                if (response.data.status === 'success') {
                                    this.getUsers();
                                    this.$notification('success', 'Customer created successfull');
                                    this.closeCreateCustomer();
                                } else {
                                    this.$notification('error', response.data.message);
                                }
                            });
                    } else {
                        this.isProcessing = false;
                        this.$notification('error', response.data.message.email[0]);
                    }
                });
        },
        getUsers() {
            let self = this;
            axios.post('/admin/users/list-for-booking')
                .then(response => {
                    self.users = response.data.users;

                    // Get id and name of users and push into the userOptions
                    response.data.users.forEach((user) => {
                        self.userOptions.push({id: user.id, text: user.name, name: user.name});
                    });

                    // Check if user_id available in the URL then select the user
                    const urlParams = new URLSearchParams(window.location.search);
                    const userId = urlParams.get('user_id');
                    if (userId) {
                        this.order.user_id = userId;

                        // Remove the user_id from the URL
                        const urlParams = new URLSearchParams(window.location.search);
                        urlParams.delete('user_id');
                        const newUrl = window.location.pathname + '?' + urlParams.toString();
                        window.history.replaceState(null, '', newUrl);
                        this.getContactPersons();
                    }
                });
        },
        // Get user contact persona and address
        getContactPersons() {
            let self = this;
            // Check if user_id is empty then no need to call the API
            if (self.order.user_id && String(self.order.user_id).trim() == '')
            {
                self.userContactPersons = [];
                self.addresses = [];
                self.businessOrIndividual = '';
                self.order.business_billing_address = '';
                return false;
            }

            // Call the API to get user contact persona and address
            axios.post('/admin/users/contact-persons-and-address', {user_id: self.order.user_id, status: 'active'})
                .then(response => {
                    self.userContactPersons = response.data.contact_persons;
                    self.addresses = response.data.addresses;
                    self.businessOrIndividual = response.data.user.account_type;

                    // Prepare the contact person options
                    self.contactPersonOptions = [{id: ' ', text: 'Select Contact Person', name: 'Select Contact Person'}];
                    response.data.contact_persons.forEach((contactPerson) => {
                        self.contactPersonOptions.push({id: contactPerson.id, text: contactPerson.full_name, name: contactPerson.full_name});
                    });

                    // Prepare the address options
                    self.addressOptions = [{id: ' ', text: 'Select Address', name: 'Select Address'}];
                    response.data.addresses.forEach((address) => {
                        self.addressOptions.push({id: address.id, text: address.full_address, name: address.full_address});
                    });

                    // Prefill the business billing address
                    self.order.business_billing_address = response.data.billing_address;

                    // If there is only one address then set it as default
                    if (self.addresses .length == 1) {
                        self.order.address_id = self.addresses[0].id;
                    }

                    // Check if business contact person is a only one then set it as default
                    if (self.userContactPersons.length == 1) {
                        self.order.business_contact_person = self.userContactPersons[0].id.toString();
                    }
                    self.selectUser = true;

                    // Check if address_id available in the URL then select the user
                    const urlParams = new URLSearchParams(window.location.search);
                    const addressId = urlParams.get('address_id');
                    if (addressId) {
                        this.order.address_id = addressId;

                        // Remove the address_id from the URL
                        const urlParams = new URLSearchParams(window.location.search);
                        urlParams.delete('address_id');
                        const newUrl = window.location.pathname + '?' + urlParams.toString();
                        window.history.replaceState(null, '', newUrl);
                    }
                });
        },
        getBusinessAddressContactPerson(event) {
            // First check if user is not selected then perform the action to select the user
            if (!this.selectUser) {
                this.filterUserFromAddress();
            }

            if (this.businessOrIndividual === 'business') {
                let self = this;

                // Call the API to get user contact persona and address
                axios.post('/admin/users/business-contact-persons', {user_id: self.order.user_id, address_id: self.order.address_id})
                    .then(response => {
                        self.businessAddressContactPersonOptions = [{id: ' ', text: 'Select Contact Person', name: 'Select Contact Person'}];
                        response.data.users.forEach((user) => {
                            self.businessAddressContactPersonOptions.push({id: user.id, text: user.full_name, name: user.full_name});
                        });

                        // Check if only one contact person then set it as default
                        if (Object.keys(response.data.users).length == 1) {
                            self.order.address_contact_person = response.data.users[0].id;
                        }
                    });
            }
        },
        getServicesForOrder() {
            let self = this;
            axios.post('/admin/services/get-services-for-order', {})
                .then(response => {
                    self.services = response.data.services;
                    self.packages = response.data.packages;

                    // Prepare service options
                    self.serviceOptions = [{id: '', name: 'Select Service', price: '', labour_cost: '', discount: ''}];
                    response.data.services.forEach((service) => {
                        self.serviceOptions.push({
                            id: service.id,
                            name: service.name,
                            price: service.price,
                            labour_cost: service.labour_cost,
                            discount: service.discount
                        });
                    });

                    // Prepare package options
                    self.packageOptions = [{id: ' ', text: 'Select Package'}];
                    response.data.packages.forEach((packageItem) => {
                        self.packageOptions.push({id: packageItem.id, text: packageItem.name});
                    });
                });
        },
        addMoreServiceItem() {
            this.totalServiceItems++;
            this.order.services.push({
                service_type: 'service',
                service_id: '',
                package_id: '',
                tax: 20,
                discount: '',
                amount: '',
                labour_cost: '',
                discount_amount: 0,
                tax_amount: 0.00,
                total_amount: 0.00,
                total_with_tax: 0.00,
                select_service:null
            });
        },
        calculateServiceItemAmount(type, index) {
            // let self = this;
            // let serviceDetail = {};
            // if (type == 'service') {
            //     // Get the service detail
            //     serviceDetail = self.services.find(service => service.id == self.order.services[index].service_id);
            // }
            // else if (type == 'package') {
            //     serviceDetail = this.packages.find(servicePackage => servicePackage.id == this.order.services[index].package_id);
            // }

            // // Check if serviceDetail is not empty then update the amount in order.services
            // let amount = 0;
            // let LabourCost = 0;
            // if (Object.keys(serviceDetail).length > 0) {
            //     amount = serviceDetail.price;
            //     LabourCost = serviceDetail.labour_cost;
            // }

            // this.order.services[index].amount = amount.toFixed(2);
            // this.order.services[index].labour_cost = LabourCost.toFixed(2);
            // this.order.services[index].total_amount = amount.toFixed(2);
            // this.order.services[index].total_with_tax = amount.toFixed(2);

            let self = this;
            let amount = 0;
            let LabourCost = 0;
            if (type === 'service') {
                const serviceId = this.order.services[index].service_id;
                let serviceDetail = this.services.find(service => service.id == serviceId);
                if (Object.keys(serviceDetail).length > 0) {
                    amount = Number(serviceDetail.price) || 0;
                    LabourCost = Number(serviceDetail.labour_cost) || 0;
                }
            }
            // Ensure values are not undefined or NaN
            amount = isNaN(amount) ? 0 : amount;
            LabourCost = isNaN(LabourCost) ? 0 : LabourCost;
            this.order.services[index].amount = amount.toFixed(2);
            this.order.services[index].labour_cost = LabourCost.toFixed(2);
            this.order.services[index].total_amount = amount.toFixed(2);
            this.order.services[index].total_with_tax = amount.toFixed(2);
        },
        calculateServiceTotalAmount(service) {
            // Calculate total amount for each service considering discount
            const amount                = Number(service.amount) || 0;
            const LabourCost            = Number(service.labour_cost) || 0;
            const discountPercentage    = Number(service.discount) || 0;
            const amountWithLabour      = amount + LabourCost;
            const discount              = (amountWithLabour * discountPercentage) / 100;

            // Get discount on the item price
            let itemDiscount = (amount*discountPercentage) / 100;

            // Get the discount on the labour
            let itemLabourDiscount = (LabourCost*discountPercentage) / 100;

            // Get the item price, labour price and final price after discount
            let itemTotalAmount         = amount - itemDiscount;
            let itemLabourTotalAmount   = LabourCost - itemLabourDiscount;
            let discountedAmount        = amountWithLabour - discount;

            // Apply the 20% vat on the discounted amount
            let taxRate     = service.tax;
            const taxAmount = (discountedAmount * taxRate) / 100;

            // Get the final amount
            let totalWithTax = discountedAmount + taxAmount;

            // Update the service tax amount in the order object
            service.tax_amount      = parseFloat(taxAmount).toFixed(2);
            service.discount_amount = parseFloat(discount).toFixed(2);
            service.total_amount    = parseFloat(discountedAmount).toFixed(2);
            service.total_with_tax  = parseFloat(totalWithTax).toFixed(2);

            return {
                itemTotalAmount: isNaN(itemTotalAmount) ? 0 : itemTotalAmount.toFixed(2),
                itemLabourTotalAmount: isNaN(itemLabourTotalAmount) ? 0 : itemLabourTotalAmount.toFixed(2),
                finalAmount: isNaN(discountedAmount) ? 0 : discountedAmount.toFixed(2)
            };
        },
        calculateServiceTotalDiscount(service) {
            // Calculate total discount for each service
            const amount = Number(service.amount) || 0;
            const LabourCost = Number(service.labour_cost) || 0;
            const discountPercentage = Number(service.discount) || 0;
            const amountWithLabour = amount + LabourCost;
            return (amountWithLabour * discountPercentage) / 100;
        },
        calculateTotalAmount() {
            // Calculate total amount for all services
            let totalAmount = 0;
            let totalItemAmount = 0;
            let totalLabourCost = 0;

            this.order.services.forEach((service) => {
                let serviceTotal = this.calculateServiceTotalAmount(service);

                totalItemAmount += parseFloat(serviceTotal.itemTotalAmount) || 0;
                totalLabourCost += parseFloat(serviceTotal.itemLabourTotalAmount) || 0;
                totalAmount += parseFloat(serviceTotal.finalAmount) || 0;
            });

            // Get the overall discount
            const discountPercentage    = Number(this.order.discount) || 0;
            const discount              = (totalAmount * discountPercentage) / 100;
            const discountedAmount      = totalAmount - discount;

            // Apply the 20% vat on the discounted amount
            const vat       = (discountedAmount * this.order.tax) / 100;
            let finalAmount = discountedAmount + vat;

            // Place only two decimal places
            totalAmount = isNaN(totalAmount) ? 0 : parseFloat(totalAmount).toFixed(2);
            finalAmount = isNaN(finalAmount) ? 0 : parseFloat(finalAmount).toFixed(2);

            // Update the total amount in the order object
            this.order.amount           = totalAmount;
            this.order.item_price       = isNaN(totalItemAmount) ? 0 : parseFloat(totalItemAmount).toFixed(2);
            this.order.labour_cost      = isNaN(totalLabourCost) ? 0 : parseFloat(totalLabourCost).toFixed(2);
            this.order.discount_amount  = isNaN(discount) ? 0 : parseFloat(discount).toFixed(2);
            this.order.tax_amount       = isNaN(vat) ? 0 : parseFloat(vat).toFixed(2);
            this.order.total_amount     = finalAmount;
        },
        validateInput(type, index) {
            // Ensure the entered value is not greater than 100
            if (type === 'service') {
                if (this.order.services[index].discount > 100) {
                    this.order.services[index].discount = 100;
                }
            }

            else if(type === 'order') {
                if (this.order.discount > 100) {
                    this.order.discount = 100;
                }
            }
        },
        allowNumbersOnly(event) {
            // Allow only numeric input
            const charCode = event.charCode;
            // allow only number keys and numpad number keys
            if (
                (charCode >= 48 && charCode <= 57) ||  // Regular number keys
                (charCode >= 96 && charCode <= 105) ||  // Numpad number keys
                charCode === 110 || charCode === 190  // Decimal point
            ) {
                return;
            }
        },
        handleSpecialKeys(event) {
            // Handle special keys like arrow keys, delete, and backspace
            const specialKeys = [8, 9, 46, 37, 39, 110, 190];
            if(specialKeys.includes(event.keyCode) || ((event.keyCode >= 48 && event.keyCode <= 57) ||(event.keyCode >= 96 && event.keyCode <= 105))) {
                return;
            }
            event.preventDefault();
        },
        // Create order
        async createOrder() {
            let self = this;
            this.isValidated = await this.validateOrderForm();
            /* if(!this.validateOrderForm()) {
                this.isValidated = false;
                return;
            } */
            // Check if the form is validated
            if (!this.isValidated) {
                return;
            }

            this.isValidated = true;

            this.isProcessing = true;

            const config = {
                headers: { 'content-type': 'multipart/form-data' }
            }

            // Get the files from the image uploader
            this.order.files = this.$refs.imageUploader.files.map((file) => file.file);

            axios.post('/admin/orders', this.order, config)
                .then(response => {
                    if (response.data.status === 'success') {
                        window.location.href = response.data.redirect_url;
                    }
                });
        },
        setMinDatetime() {
            // Set the minimum datetime
            this.minDatetime = moment().format('YYYY-MM-DDTHH:mm');
        },
        setMinDate() {
            this.minDate = moment().format('YYYY-MM-DD');
        },
        validateCustomerForm() {
            let isValidated = true;
            if(this.customer.first_name.trim() == ''){
                isValidated = false;
            }
            if(this.customer.last_name.trim() == ''){
                isValidated = false;
            }
            // if(!(/\S+@\S+\.\S+/.test(this.customer.email.trim()))){
            //     isValidated = false;
            // }
            if(this.customer.phone_number == ''){
                isValidated = false;
            }
            if(this.customer.address.address.trim() == ''){
                isValidated = false;
            }
            // if(this.customer.address.address_1.trim() == ''){
            //     isValidated = false;
            // }
            if(this.customer.address.city.trim() == ''){
                isValidated = false;
            }
            if(this.customer.address.county.trim() == ''){
                isValidated = false;
            }
            if(this.customer.address.postal_code.trim() == ''){
                isValidated = false;
            }
            return isValidated;
        },
        // Validate form, check all required fields are filled
        async validateOrderForm() {
            let isValidated = true;
            if(this.order.user_id && String(this.order.user_id).trim() == '') {
                isValidated = false;
            }

            if(typeof this.order.address_id === 'string' && this.order.address_id.trim() == '') {
                isValidated = false;
            }

            // if(this.businessOrIndividual === 'business' && this.order.business_contact_person.trim() == '') {
            //     isValidated = false;
            // }

            if(this.businessOrIndividual === 'business' && this.order.business_billing_address.trim() == '') {
                isValidated = false;
            }

            // if(this.businessOrIndividual === 'business' && this.order.address_contact_person.trim() == '') {
            //     isValidated = false;
            // }

            //if(this.order.booking_date.trim() == '') {
            //    isValidated = false;
            //}

            // if(this.order.services.length == 0) {
            //     isValidated = false;
            // }

            // Iterate the services and check if select_service is null then set isValidated to false
            for (let i = 0; i < this.order.services.length; i++) {
                if(this.order.services[i].select_service == null) {
                    isValidated = false;
                }
            }

            return isValidated;
        },
        onFileChange(event) {
            console.log(event.target.files);
            for (let file of event.target.files) {
                try {
                    let reader = new FileReader();

                    // Display the image preview
                    reader.onload = (e) => {
                        this.previewOrderImages.push(e.target.result);
                    }
                    reader.readAsDataURL(file); // Not sure if this will work in this context.
                    this.order.files.push(file);
                } catch {}
            }
        },

        async validateCreateAddressForm() {
            // Reset errors
            this.createAddressError.person_type = null;
            this.createAddressError.salutation = null;
            this.createAddressError.first_name = null;
            this.createAddressError.email = null;
            this.createAddressError.phone_number = null;
            this.createAddressError.address = null;
            this.createAddressError.city = null;
            this.createAddressError.county = null;
            this.createAddressError.postal_code = null;


            // Validate each field
            // const personTypeValidation = await validate(this.createCustomerAddress.person_type, { required });
            // const salutationValidation = await validate(this.createCustomerAddress.salutation, { required });
            // const firstNameValidation = await validate(this.createCustomerAddress.first_name, { required });
            // const emailValidation = await validate(this.createCustomerAddress.email, { required, email });
            // const phoneNumberValidation = await validate(this.createCustomerAddress.phone_number, { required });
            // const addressValidation = await validate(this.createCustomerAddress.address, { required });
            // const cityValidation = await validate(this.createCustomerAddress.city, { required });
            // const countyValidation = await validate(this.createCustomerAddress.county, { required });
            // const postalCodeValidation = await validate(this.createCustomerAddress.postal_code, { required });

            // // Update error messages
            // if (!personTypeValidation.valid) {
            //     this.createAddressError.person_type = personTypeValidation.errors[0];
            // }
            // if (!salutationValidation.valid) {
            //     this.createAddressError.salutation = salutationValidation.errors[0];
            // }
            // if (!firstNameValidation.valid) {
            //     this.createAddressError.first_name = firstNameValidation.errors[0];
            // }
            // if (!emailValidation.valid) {
            //     this.createAddressError.email = emailValidation.errors[0];
            // }
            // if (!phoneNumberValidation.valid) {
            //     this.createAddressError.phone_number = phoneNumberValidation.errors[0];
            // }
            // if (!addressValidation.valid) {
            //     this.createAddressError.address = addressValidation.errors[0];
            // }
            // if (!cityValidation.valid) {
            //     this.createAddressError.city = cityValidation.errors[0];
            // }
            // if (!countyValidation.valid) {
            //     this.createAddressError.county = countyValidation.errors[0];
            // }
            // if (!postalCodeValidation.valid) {
            //     this.createAddressError.postal_code = postalCodeValidation.errors[0];
            // }
        },
        addNewAddress() {
            $('#addNewAddressModal').modal('show');
        },
        processCreateAddress() {
            let self = this;
            // Basic fields validation
            this.validationAddressErrors = [];
            const fieldsToCheck = ['address', 'city', 'county', 'postal_code'];
            fieldsToCheck.forEach(field => {
                if (!this.createCustomerAddress[field]) {
                    this.validationAddressErrors.push({key: field, message: `${field} is required.`});
                }
            });

            // Contact persons validation only for business
            if (this.businessOrIndividual === 'business') {
                this.createCustomerAddress.contact_person.forEach((person, index) => {
                    const baseErrorMessage = `Contact person ${index + 1}: `;

                    if (!person.first_name) {
                        this.validationAddressErrors.push({key: 'first_name_' + index, message: 'First name is required.'});
                    }

                    if (!person.email) {
                        this.validationAddressErrors.push({key: 'email_' + index, message: 'Email is required.'});
                    } else if (!this.isValidEmail(person.email)) {
                        this.validationAddressErrors.push({key: 'email_' + index, message: 'Email is invalid.'});
                    }

                    if (!person.phone_number) {
                        this.validationAddressErrors.push({key: 'phone_number_' + index, message: 'Phone number is required.'});
                    }
                });
            }

            if (this.validationAddressErrors.length > 0) {
                this.isAddressFormValid = false;
                return;
            }

            // Submit the form
            this.isAddressFormValid = true;
            this.createAddressProcessing = true;

            axios.post('/admin/users/store-address/' + this.order.user_id, this.createCustomerAddress)
                .then(response => {
                    if (response.data.status === 'success') {
                        self.addressOptions.push({id: response.data.address.id, text: response.data.address.complete_address, name: response.data.address.complete_address});

                        // selected the new created address
                        self.order.address_id = response.data.address.id;

                        // Call the API to get user contact persona and address
                        self.getBusinessAddressContactPerson();
                        self.createAddressProcessing = false;
                        // Close the modal
                        $('#createAddressModal').modal('hide');
                    }
                });
        },
        isValidEmail(email) {
            const regex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/; // Simple email regex for demonstration
            return regex.test(email);
        },

        // Get the error message
        getErrorMessage(key) {
            const error = this.validationAddressErrors.find(error => error.key === key);
            return error ? error.message : '';
        },
        addMoreAddressContactPerson() {
            this.createCustomerAddress.contact_person.push({
                person_type: '',
                salutation: '',
                first_name: '',
                last_name: '',
                email: '',
                phone_number: '',
                phone_number_1: '',
            });
        },
        prefillContactPerson(event, index) {
            let isChecked = event.target.checked;
            // Check if index is 0 then get the first contact person
            if (this.userContactPersons.length > 0) {
                // Check if index exists in the array
                if (isChecked) {
                    if (this.userContactPersons[index]) {
                        this.createCustomerAddress.contact_person[index].person_type = 'property_owner';
                        this.createCustomerAddress.contact_person[index].salutation = this.userContactPersons[index].salutation;
                        this.createCustomerAddress.contact_person[index].first_name = this.userContactPersons[index].first_name;
                        this.createCustomerAddress.contact_person[index].last_name = this.userContactPersons[index].last_name;
                        this.createCustomerAddress.contact_person[index].email = this.userContactPersons[index].email;
                        this.createCustomerAddress.contact_person[index].phone_number = this.userContactPersons[index].phone_number;
                        this.createCustomerAddress.contact_person[index].phone_number_1 = this.userContactPersons[index].phone_number_1;
                    }
                    else {
                        this.createCustomerAddress.contact_person[index].person_type = 'property_owner';
                        this.createCustomerAddress.contact_person[index].salutation = this.userContactPersons[0].salutation;
                        this.createCustomerAddress.contact_person[index].first_name = this.userContactPersons[0].first_name;
                        this.createCustomerAddress.contact_person[index].last_name = this.userContactPersons[0].last_name;
                        this.createCustomerAddress.contact_person[index].email = this.userContactPersons[0].email;
                        this.createCustomerAddress.contact_person[index].phone_number = this.userContactPersons[0].phone_number;
                        this.createCustomerAddress.contact_person[index].phone_number_1 = this.userContactPersons[0].phone_number_1;
                    }
                }
                else {
                    this.createCustomerAddress.contact_person[index].person_type = '';
                    this.createCustomerAddress.contact_person[index].salutation = '';
                    this.createCustomerAddress.contact_person[index].first_name = '';
                    this.createCustomerAddress.contact_person[index].last_name = '';
                    this.createCustomerAddress.contact_person[index].email = '';
                    this.createCustomerAddress.contact_person[index].phone_number = '';
                    this.createCustomerAddress.contact_person[index].phone_number_1 = '';
                }
            }
        },
        getAllAddresses() {
            let self = this;
            axios.post('/admin/users/get-address-list',)
                .then(response => {
                    if (response.data.status === 'success') {
                        self.addresses = response.data.addresses;

                        // Prepare the address options
                        response.data.addresses.forEach((address) => {
                            self.addressOptions.push({id: address.id, text: address.full_address, name: address.full_address});
                        });
                    }
                });
        },
        // This function will filter the user from address
        // And will show the selected user in the dropdown
        // This function will be called when user select the address without selecting the user
        filterUserFromAddress() {
            if (this.order.address_id) {
                let address = this.addresses.find(address => Number(address.id) === Number(this.order.address_id));
                if (address) {
                    this.order.user_id = address.user_id.toString();

                    // Filter the user and set the value for the business or individual
                    let user = this.users.find(user => Number(user.id) == Number(address.user_id));
                    if (user) {
                        this.businessOrIndividual = user.account_type;

                        // Set the value for the business or individual
                        this.order.business_billing_address = user.billing_address;

                        // Fill the contact person option
                        if (Object.keys(user.contact_persons).length > 0) {
                            this.userContactPersons = user.contact_persons;
                            user.contact_persons.forEach((contactPerson) => {
                                this.contactPersonOptions.push({id: contactPerson.id, text: contactPerson.full_name});
                            });

                            // Check if only one contact person then set it as default
                            if (Object.keys(user.contact_persons).length == 1) {
                                this.order.business_contact_person = user.contact_persons[0].id;
                            }
                            else {
                                this.order.business_contact_person = '';
                            }
                        }
                    }
                }
            }
        },
        addNewService(newService, index) {
            let serviceTag = {
                id: '',
                name: newService,
                price: 0.00,
                labour_cost: 0.00,
                discount: ''
            };
            this.serviceOptions.push(serviceTag);
            this.services.push(serviceTag);

            this.order.services[index].name = newService;
            this.order.services[index].select_service = serviceTag;
        },
        selectService(event, index) {
            //this.calculateServiceItemAmount('service', item - 1)
            //console.log(event, index)
            this.order.services[index].service_id = event.id || '';
            this.order.services[index].name = event.name || '';
            this.order.services[index].amount = event.price ? event.price.toFixed(2) : 0.00;
            this.order.services[index].labour_cost = event.labour_cost ? event.labour_cost.toFixed(2) : 0.00;
            this.order.services[index].discount = event.discount || '';
        },
        async getHandymanAvailable() {
            let params = {
                booking_date: this.order.booking_date,
                start_time: this.order.booking_time,
                end_time: {
                    hours: (parseInt(this.order.booking_time.hours) + 1) % 24,
                    minutes: parseInt(this.order.booking_time.minutes) || 0
                },
            };
            let users = await this.getHandymanForTaskAssignment(params);

            //Check if user is empty then empty the handyman_ids
            if (users.length == 0) {
                this.order.handyman_ids = [];
            }
            //Prepare the options
            this.handymanList = users.map(user => ({
                id: user.id,
                text: user.name,
                name: user.name,
            }));
        },
        handleWorkOrderFileChange(files) {
            if (files && files.length > 0) {
                // Initially clear any previous error message

                this.uploadWorkOrder.error = '';
                this.uploadWorkOrder.processing = true;
                this.uploadWorkOrder.success = false;

                const fileData = files[0];
                // Store file information
                this.uploadWorkOrder.fileName = fileData.name;
                this.uploadWorkOrder.fileType = fileData.type;
                // Extract base64 data from preview (which is already base64)
                // The preview format is: data:image/jpeg;base64,/9j/4AAQ...
                const base64WithPrefix = fileData.preview;
                // Remove the data URL prefix to get pure base64
                const base64Data = base64WithPrefix.split(',')[1];
                this.uploadWorkOrder.base64Data = base64Data;
                // console.log('File info:', {
                //     name: this.uploadWorkOrder.fileName,
                //     type: this.uploadWorkOrder.fileType,
                //     size: fileData.size,
                //     base64Length: base64Data.length
                // });
                // TODO: Call API to extract data from the work order
                this.extractWorkOrderData();
            }
        },
        extractWorkOrderData() {
            this.uploadWorkOrder.processing = true;
            axios.post('/api/admin/orders/extract-work-order-data', {
                fileName: this.uploadWorkOrder.fileName,
                fileType: this.uploadWorkOrder.fileType,
                base64Data: this.uploadWorkOrder.base64Data
            })
                .then(response => {
                    if (response.data.status === 'success') {
                        this.uploadWorkOrder.success = true;
                        this.uploadWorkOrder.extractedData = response.data.extracted_data;
                        this.uploadWorkOrder.matchedAddress = response.data.matched_address;
                        this.uploadWorkOrder.matchedService = response.data.matched_service;

                        // Fill the booking date if required_by is not empty
                        if (this.uploadWorkOrder.extractedData.required_by) {
                            this.order.booking_date = this.moment(this.uploadWorkOrder.extractedData.required_by, 'DD/MM/YYYY').format('YYYY-MM-DD');
                        }

                        // if address is matched then set the address id
                        if (this.uploadWorkOrder.matchedAddress) {
                            this.order.address_id = this.uploadWorkOrder.matchedAddress.id;
                            this.filterUserFromAddress();
                        }

                        // If service is matched then set the service id
                        if (this.uploadWorkOrder.matchedService) {
                            this.order.services[0].service_id = this.uploadWorkOrder.matchedService.id;
                            this.order.services[0].name = this.uploadWorkOrder.matchedService.name;
                            this.order.services[0].amount = this.uploadWorkOrder.matchedService.price || 0.00;
                            this.order.services[0].labour_cost = this.uploadWorkOrder.matchedService.labour_cost || 0.00;
                            this.order.services[0].discount = this.uploadWorkOrder.matchedService.discount || 0.00;
                            this.order.services[0].select_service = {
                                id: this.uploadWorkOrder.matchedService.id,
                                name: this.uploadWorkOrder.matchedService.name,
                                price: this.uploadWorkOrder.matchedService.price,
                                labour_cost: this.uploadWorkOrder.matchedService.labour_cost,
                                discount: this.uploadWorkOrder.matchedService.discount
                            };
                        }
                        else {
                            let serviceName = this.uploadWorkOrder?.extractedData?.service || '';
                            let serviceTag = {
                                id: '',
                                name: serviceName,
                                price: this.uploadWorkOrder?.extractedData?.sub_total || 0.00,
                                labour_cost: this.uploadWorkOrder?.extractedData?.labour_cost || 0.00,
                                discount: this.uploadWorkOrder?.extractedData?.discount || 0.00
                            };
                            this.serviceOptions.push(serviceTag);
                            this.services.push(serviceTag);

                            this.order.services[0].amount = this.uploadWorkOrder?.extractedData?.sub_total || 0.00;
                            this.order.services[0].labour_cost = this.uploadWorkOrder?.extractedData?.labour_cost || 0.00;
                            this.order.services[0].discount = this.uploadWorkOrder?.extractedData?.discount || 0.00;
                            this.order.services[0].name = serviceName;
                            this.order.services[0].select_service = serviceTag;
                        }
                    }
                })
                .catch(error => {
                    this.uploadWorkOrder.error = error.response.data.message;
                })
                .finally(() => {
                    console.log('Processing completed');
                    this.uploadWorkOrder.processing = false;

                    // Clear the selected file
                    //this.$refs.uploadWordOrder.clearFiles();
                });
        },
    },
    watch: {
        'order.services': {
            handler(newServices, oldServices) {
                // Recalculate the total amount and total discount for all services
                this.calculateTotalAmount();
            },
            deep: true,
        },
        'order.discount': function(newDiscount, oldDiscount) {
            // Recalculate the total amount for all services
            this.calculateTotalAmount();
        },
        // When change the TAX amount
        'order.tax': function(newTax, oldTax) {
            // Recalculate the total amount for all services
            this.calculateTotalAmount();
        },
        'order.booking_date': function(newVal, oldVal) {
            // Emit the event to date is changed
            this.$emitter.emit('service_date_changed', {date: newVal, time: this.order.booking_time});
            this.getHandymanAvailable();
        },
        'order.booking_time': function(newVal, oldVal) {
            // Emit the event to time is changed
            this.$emitter.emit('service_time_changed', {date: this.order.booking_date, time: newVal});
            this.getHandymanAvailable();
        },

    }
}
</script>
<style>
.select2-container--open .select2-dropdown--below{
    z-index: 99999;
}
</style>
